import { describe } from '@servicetim/ui/components';
import { ApiError } from '@servicetim/ui/lib';

/** Every dotted path of a nested values object (`settings`, `settings.branding.primaryColor`, `scopes`). */
export function flattenPaths(values: unknown, prefix = '', out = new Set<string>()): Set<string> {
  if (values === null || typeof values !== 'object' || Array.isArray(values)) {
    if (prefix) out.add(prefix);
    return out;
  }
  for (const [key, value] of Object.entries(values as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key;
    out.add(path);
    flattenPaths(value, path, out);
  }
  return out;
}

const ancestors = (path: string): string[] => {
  const segments = path.split('.');
  const out: string[] = [];
  for (let n = segments.length - 1; n > 0; n -= 1) out.push(segments.slice(0, n).join('.'));
  return out;
};

/**
 * Pushes problem+json `errors[]` onto the form: exact path first, then the nearest known ancestor
 * (`scopes.2` → `scopes`; `config.maxLength` stays exact). Returns the raw paths that were mapped
 * so the banner can omit them; unmapped errors stay in the banner.
 */
export function applyProblemErrors(
  error: unknown,
  setError: (path: string, message: string) => void,
  known: ReadonlySet<string>,
): Set<string> {
  const mapped = new Set<string>();
  if (!(error instanceof ApiError)) return mapped;
  for (const fieldError of error.fieldErrors) {
    const target = [fieldError.path, ...ancestors(fieldError.path)].find((p) => known.has(p));
    if (target) {
      setError(target, fieldError.message);
      mapped.add(fieldError.path);
    }
  }
  return mapped;
}

/**
 * One-line message for a thrown error, for confirm dialogs and toasts: the first field error when
 * the API sent one (`role.in_use: the role is referenced by permission grants`), else the detail.
 */
export function problemMessage(error: unknown): string {
  const view = describe(error);
  const field = view.fieldErrors[0];
  const head = view.detail ?? view.title;
  return field ? `${head}: ${field.message}` : head;
}

/** Re-throws with a readable message so `useConfirm({ onConfirm })` shows it inline. */
export async function withProblemMessage<T>(action: () => Promise<T>): Promise<T> {
  try {
    return await action();
  } catch (error) {
    throw new Error(problemMessage(error));
  }
}
